<template>
  <el-row :gutter="16" style="padding-bottom: 16px">
    <template v-for="grupo in grupos" :key="grupo.grupo">
      <el-col :span="24" style="margin: 1rem 0">
        <PDivider :label="grupo.grupo" />
      </el-col>
      <template v-for="campo in grupo.campos" :key="campo.nomeCampo">
        <el-col
          v-if="isVisibleField(campo)"
          :span="getRenderer(campo).tamanho"
          style="margin-bottom: 16px"
          @focusout="markFieldAsTouched(campo.nomeCampo)"
        >
          <component
            v-if="needsActionProps(campo)"
            :is="getRenderer(campo).component"
            :campo="campo"
            :model-value="getFieldValue(campo)"
            :error="visibleErrors[campo.nomeCampo]"
            :form-values="values"
            :action-context="actionContext"
            :set-field-value="setFieldValue"
            :execute-field-action="executeFieldAction"
            :request-close-drawer="requestCloseDrawer"
            :request-reload-form="requestReloadForm"
            @update:model-value="
              (v: unknown) => setFieldValue(campo.nomeCampo, v)
            "
            @update:selected-options="atualizarOpcoesSelecionadas"
          />
          <component
            v-else
            :is="getRenderer(campo).component"
            :campo="campo"
            :model-value="getFieldValue(campo)"
            :error="visibleErrors[campo.nomeCampo]"
            :form-values="values"
            @update:model-value="
              (v: unknown) => setFieldValue(campo.nomeCampo, v)
            "
            @update:selected-options="atualizarOpcoesSelecionadas"
          />
          <FieldHint :hint="campo.hint" />
        </el-col>
      </template>
    </template>
  </el-row>
</template>

<script setup lang="ts">
import { PDivider } from "@f360-dev/f360-prisma";
import { useForm } from "vee-validate";
import { computed, ref, watch } from "vue";
import {
  SELECTED_OPTIONS_BY_FIELD_KEY,
  TIntegracaoCampoModel,
  TIntegracaoCampoOpcao,
  TIntegracaoOpcoesSelecionadasPorCampo,
  TIntegracaoCamposAgrupados
} from "../../@types/IntegracaoCampoModel";
import { ObjectSchema } from "yup";
import useBuildInitialValues from "../../composables/dynamicForm/handlers/useBuildInitialValues/useBuildInitialValues";
import useDisplayRule from "../../composables/dynamicForm/handlers/useDisplayRule/useDisplayRule";
import useFieldRegistry from "../../composables/dynamicForm/handlers/useFieldRegistry/useFieldRegistry";
import useDynamicForm from "../useDynamicForm";
import FieldHint from "../fields/FieldHint.vue";
import { CadastroDeIntegracaoGateway } from "../../services/CadastroDeIntegracaoGateway";
import { ETipoVisualizacao } from "../../@types/IntegracaoCampoEnum";

const props = defineProps<{
  campos: TIntegracaoCampoModel[];
  grupos: TIntegracaoCamposAgrupados;
  schema: ObjectSchema<Record<string, unknown>>;
  initialValues: Record<string, unknown>;
  gateway: CadastroDeIntegracaoGateway;
  actionContext?: Record<string, unknown>;
  requestReloadForm?: () => Promise<void>;
}>();

const emit = defineEmits<{ (e: "closeDrawer"): void }>();

const executeFieldAction: CadastroDeIntegracaoGateway["executarAcaoCampo"] = (
  metodo,
  endpoint,
  payload
) => props.gateway.executarAcaoCampo(metodo, endpoint, payload);

const requestCloseDrawer = () => emit("closeDrawer");

const {
  values,
  errors,
  setFieldValue,
  setValues,
  handleSubmit,
  submitCount,
  resetForm
} = useForm({
  validationSchema: computed(() => props.schema),
  initialValues: props.initialValues
});

const touchedFields = ref<Record<string, boolean>>({});

const markFieldAsTouched = (fieldName: string) => {
  touchedFields.value[fieldName] = true;
};

const fieldHasValue = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length > 0;

  return (
    value !== null && value !== undefined && value !== "" && value !== false
  );
};

const visibleErrors = computed<Record<string, string | undefined>>(() => {
  if (submitCount.value > 0) return errors.value;

  return Object.fromEntries(
    Object.entries(errors.value).filter(
      ([fieldName]) =>
        touchedFields.value[fieldName] && fieldHasValue(values[fieldName])
    )
  );
});

const { resolveRenderer } = useFieldRegistry();

const fieldNeedsActionProps = (tipo: ETipoVisualizacao): boolean =>
  tipo === ETipoVisualizacao.BUTTON ||
  tipo === ETipoVisualizacao.REMOTE_COLLECTION;

const { isFieldVisible } = useDisplayRule();

const getRenderer = (campo: TIntegracaoCampoModel) => resolveRenderer(campo);

const getFieldValue = (campo: TIntegracaoCampoModel) => values[campo.nomeCampo];

const isVisibleField = (campo: TIntegracaoCampoModel) =>
  isFieldVisible(campo, values as Record<string, unknown>);

const needsActionProps = (campo: TIntegracaoCampoModel): boolean =>
  fieldNeedsActionProps(getRenderer(campo).tipo);

const atualizarOpcoesSelecionadas = (payload: {
  fieldName: string;
  options: TIntegracaoCampoOpcao[];
}) => {
  const { fieldName, options: selectedOptions } = payload;
  const currentMap =
    (values[
      SELECTED_OPTIONS_BY_FIELD_KEY
    ] as TIntegracaoOpcoesSelecionadasPorCampo) ?? {};

  setFieldValue(
    SELECTED_OPTIONS_BY_FIELD_KEY,
    {
      ...currentMap,
      [fieldName]: selectedOptions
    },
    false
  );
};

const valorVazioIntegracaoCampo = (campo: TIntegracaoCampoModel): unknown =>
  useBuildInitialValues().initialValueByType(campo);

const { valoresEquivalentesParaReset, nomesCamposControleRegra } =
  useDynamicForm(() => props.campos);

watch(
  () => props.initialValues,
  (v) => setValues(v, false),
  { deep: true }
);

const resetarCamposControleRegra = () => {
  const valoresPorNomeCampo = values as Record<string, unknown>;

  for (const campo of props.campos) {
    if (!campo.regraExibicao) continue;
    if (isFieldVisible(campo, valoresPorNomeCampo)) continue;

    const valorVazio = valorVazioIntegracaoCampo(campo);
    const valorAtual = valoresPorNomeCampo[campo.nomeCampo];
    if (valoresEquivalentesParaReset(valorAtual, valorVazio)) continue;

    setFieldValue(campo.nomeCampo, valorVazio, false);
  }
};

watch(
  () => [
    ...nomesCamposControleRegra.value.map((nomeCampo) => values[nomeCampo]),
    values[SELECTED_OPTIONS_BY_FIELD_KEY]
  ],
  () => {
    resetarCamposControleRegra();
  },
  { deep: true, flush: "post" }
);

const resetFormState: typeof resetForm = (...args) => {
  touchedFields.value = {};

  return resetForm(...args);
};

defineExpose({ handleSubmit, values, errors, resetForm: resetFormState });
</script>

<style scoped lang="scss" src="./DynamicIntegracaoForm.scss"></style>
